import React from 'react';
import { useTranslation } from 'react-i18next';
import { FundListFilterConfig } from './useFundListFilter';
import { Chip } from '../Chip/Chip';
import { FilterActionPayload } from './tools/FundList.model';
import { getGenerateAutoTestId, AutoTestIdPrefix, GenerateAutoTestIdFn } from '../utils/testAutomation.utils';

import useStyles from './FundList.style';

export interface FundListFilterChipsProps {
  actions: { [index: string] : (payload?: FilterActionPayload) => void };
  filterConfig: FundListFilterConfig;
  isTestAutomationEnabled?: boolean;
}

const fundTypeOptionsKey = 'fund-list.options.fundType';
const riskOptionsKey = 'fund-list.options.risk';
const umbrellaOptionsKey = 'fund-list.options.umbrella';
const horizonOptionsKey = 'fund-list.options.horizon';
const horizonLabelKey = 'fund-list.horizon';
const containsTextKey = 'fund-list.contains-text';
const adequacyLabelKey = 'fund-list.options.adequacy-with-target-market.label';
const walletLabelKey = 'fund-list.options.wallet.label';
const esgLabelKey = 'fund-list.options.esg.label';
const clearFilterKey = 'fund-list.clear-filter';

const FundListFilterChips: React.FC<FundListFilterChipsProps> = ({
  actions,
  filterConfig,
  isTestAutomationEnabled = false,
}) => {
  const isDarkTheme = false;
  const c = useStyles({ isDarkTheme });
  const [t] = useTranslation('components');
  const generateAutoTestId = getGenerateAutoTestId(isTestAutomationEnabled);

  const handleFilterDelete = (filterType: string, value?: string | string[]) => {
    switch (filterType) {
      case 'fundType':
          filterConfig.fundType.filterDelete(value);
          break;
      case 'risk':
          filterConfig.risk.filterDelete(value);
          break;
      case 'umbrella':
          filterConfig.umbrella.filterDelete(value);
          break;
      case 'horizon':
          filterConfig.horizon.filterDelete(value);
          break;
      case 'search':
          filterConfig.search.clearFilter();
          break;
      case 'autocomplete':
          filterConfig.autocomplete.clearFilter();
          break;
      case 'adequacyWithTargetMarket':
          filterConfig.adequacyWithTargetMarket.clearFilter();
          break;
      case 'showOnlyInWallet':
          filterConfig.wallet.clearFilter();
          break;
      case 'showOnlyEsg':
          filterConfig.esg.clearFilter();
          break;
      default:
          break;
    }

    actions.removeFilter({ value, filterType });
  };

  return (
    <div className={c.chipWrapper}>
      <ChipsList
        handleDelete={handleFilterDelete}
        filters={filterConfig.fundType.value}
        type="fundType"
        generateAutoTestId={generateAutoTestId}
        labelKeyPrefix={fundTypeOptionsKey}
      />
      <ChipsList
        handleDelete={handleFilterDelete}
        filters={filterConfig.risk.value}
        type="risk"
        generateAutoTestId={generateAutoTestId}
        labelKeyPrefix={riskOptionsKey} />
      <ChipsList
        handleDelete={handleFilterDelete}
        filters={filterConfig.umbrella.value}
        type="umbrella"
        generateAutoTestId={generateAutoTestId}
        labelKeyPrefix={umbrellaOptionsKey} />
      <ChipsList
        valueToLabel={value => `${t(horizonLabelKey)} - ${t(`${horizonOptionsKey}.${value}`)}`}
        handleDelete={handleFilterDelete}
        filters={filterConfig.horizon.value}
        type="horizon"
        generateAutoTestId={generateAutoTestId}
        labelKeyPrefix={horizonOptionsKey}/>
      {filterConfig.search.value &&
        <Chip
          label={`${t(containsTextKey)}: "${filterConfig.search.value}"`}
          onDelete={() => handleFilterDelete('search')}
          autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, containsTextKey)} />}
      {filterConfig.autocomplete.value.title &&
        <Chip
          label={`${t(containsTextKey)}: "${filterConfig.autocomplete.value.title}"`}
          onDelete={() => handleFilterDelete('autocomplete')}
          autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, containsTextKey)} />}
      {filterConfig.adequacyWithTargetMarket.value &&
        <Chip
          label={t(adequacyLabelKey)}
          onDelete={() => handleFilterDelete('adequacyWithTargetMarket')}
          autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, adequacyLabelKey)} />}
      {filterConfig.wallet.value &&
        <Chip
          label={t(walletLabelKey)}
          onDelete={() => handleFilterDelete('showOnlyInWallet')}
          autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, walletLabelKey)} />}
      {filterConfig.esg.value &&
        <Chip
          label={t(esgLabelKey)}
          onDelete={() => handleFilterDelete('showOnlyEsg')}
          autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, esgLabelKey)} />}
      <Chip
        variant="clearFilters" label={t(clearFilterKey)}
        onDelete={() => filterConfig.clearFilters()}
        autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, clearFilterKey)} />
    </div>
  );
};

export interface ChipsListProps {
  filters: string[];
  handleDelete: (type: string, value: string) => void;
  type: string;
  valueToLabel?: (value: string) => string;
  generateAutoTestId?: GenerateAutoTestIdFn;
  labelKeyPrefix?: string;
}

const ChipsList: React.FC<ChipsListProps> = ({ filters, handleDelete, type, valueToLabel, generateAutoTestId, labelKeyPrefix }) => {
  if (!filters.length) {
    return null;
  }

  return <>
    {filters.map(value => (
      <Chip
        key={value}
        label={valueToLabel? valueToLabel(value) : value}
        onDelete={() => handleDelete(type, value)}
        autoTestId={generateAutoTestId?.(AutoTestIdPrefix.CHIP, `${labelKeyPrefix}`)}
      />
    ))}
  </>;
};

export default FundListFilterChips;
